import React, { useCallback, useState } from 'react';

import { GetBookDocument, GetBookQuery } from 'features/book/queries.gen';
import { useUpdateChapterMutation } from 'features/chapter/update/queries.gen';
import { ChapterByIdDocument } from 'features/story-editor/header/queries.gen';
import {
	BookProperty,
	ButtonType,
	trackPropertyChanged,
} from 'shared/analytics';
import { useAppToast } from 'shared/hooks/toast';
import { ArrayElement } from 'shared/model';
import { FormTextArea, FormWithChildren } from 'shared/ui';

import { updateDescriptionSchema } from '../../schema';

export interface Props {
	chapter: ArrayElement<GetBookQuery['book']['chapters']>;
}

export const UpdateDescriptionForm: React.FC<Props> = ({ chapter }) => {
	const toast = useAppToast();
	const [isRedacting, setIsRedacting] = useState(false);

	const onStartRedacting = useCallback(() => setIsRedacting(true), []);
	const onStopRedacting = useCallback(() => setIsRedacting(false), []);

	const [updateChapter] = useUpdateChapterMutation({
		onCompleted: () => {
			onStopRedacting();

			toast({
				title: 'Chapter description updated successfully',
				status: 'success',
			});
		},
		onError: () =>
			toast({
				title: 'Chapter description was not updated',
				status: 'error',
			}),
		refetchQueries: [ChapterByIdDocument, GetBookDocument],
	});

	const updateChapterDescription = useCallback(
		async (formValue: string) => {
			await updateChapter({
				variables: {
					chapter: {
						id: chapter?.id,
						description: formValue,
					},
				},
			});
			trackPropertyChanged({
				entity: 'chapter',
				property: BookProperty.DESCRIPTION,
				value: formValue,
				id: chapter?.id,
			});
		},
		[chapter?.id, updateChapter],
	);

	return (
		<FormWithChildren
			submitButtonName={ButtonType.EDIT_CHAPTER_DESCRIPTION}
			initialValue={chapter?.description}
			isRedacting={isRedacting}
			startRedacting={onStartRedacting}
			stopRedacting={onStopRedacting}
			validationSchema={updateDescriptionSchema}
			onSubmit={({ formValue }) => updateChapterDescription(formValue)}
		>
			<FormTextArea
				label="Description"
				name="formValue"
				placeholder="Description"
			/>
		</FormWithChildren>
	);
};
